import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { CONTACT } from '../config/products';
import { cn } from '../lib/utils';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { 
  Copy, Printer, MessageCircle, RotateCcw, Check, Package, Palette, 
  Layers, Maximize, Paintbrush2, Shield, Sparkles, Receipt, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export const Summary = ({ 
  substrateDetails,
  selectedSize,
  area,
  selectedColor, 
  textureType,
  includeSealer,
  selectedPrimer,
  calculations,
  generateSummary,
  onStartOver 
}) => {
  const { t, isRTL, language } = useLanguage();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const summary = generateSummary(language);
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      toast.success(t('copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(t('error'));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleWhatsApp = () => {
    const summary = generateSummary(language);
    const encoded = encodeURIComponent(summary);
    window.open(`https://wa.me/${CONTACT.whatsapp}?text=${encoded}`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Main Summary Card - Tech/Modern Style */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="overflow-hidden bg-gradient-to-br from-card via-card to-secondary/20 border-border/50">
          {/* Color Header Bar */}
          {selectedColor && (
            <motion.div 
              className="h-3 w-full"
              style={{ background: selectedColor.gradient || selectedColor.hex }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            />
          )}

          <CardContent className="p-4 sm:p-6 space-y-5">
            {/* Selection Summary - Compact Grid */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                { icon: Layers, label: isRTL ? 'משטח' : 'Surface', value: isRTL ? substrateDetails?.nameHe : substrateDetails?.nameEn },
                { icon: Maximize, label: isRTL ? 'שטח' : 'Area', value: `${area} ${t('areaUnit')}` },
                { icon: Palette, label: isRTL ? 'גוון' : 'Color', value: isRTL ? selectedColor?.nameHe : selectedColor?.nameEn },
                { icon: Sparkles, label: isRTL ? 'גימור' : 'Finish', value: textureType === 'textured' ? (isRTL ? 'סמיך' : 'Thick') : (isRTL ? 'דליל' : 'Diluted') },
              ].filter(item => item.value).map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + idx * 0.05 }}
                  className="flex items-center gap-2 p-2 rounded-lg bg-secondary/30"
                >
                  <item.icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{item.label}</p>
                    <p className="font-medium text-foreground truncate">{item.value}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <Separator className="bg-border/50" />

            {/* Materials Breakdown - Clean Table Style */}
            {calculations && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="space-y-3"
              >
                <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <Receipt className="w-4 h-4 text-accent" />
                  {isRTL ? 'פירוט חומרים ומחירים' : 'Materials & Pricing'}
                </div>

                <div className="rounded-xl border border-border/50 overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-2 p-3 bg-secondary/50 text-xs font-medium text-muted-foreground">
                    <div className="col-span-5">{isRTL ? 'מוצר' : 'Product'}</div>
                    <div className="col-span-3 text-center">{isRTL ? 'כמות' : 'Qty'}</div>
                    <div className="col-span-4 text-end">{isRTL ? 'מחיר' : 'Price'}</div>
                  </div>

                  {/* Primer Row */}
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="grid grid-cols-12 gap-2 p-3 border-t border-border/30 items-center"
                  >
                    <div className="col-span-5 flex items-center gap-2">
                      <Paintbrush2 className="w-4 h-4 text-warning flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-foreground text-sm truncate">
                          {isRTL ? selectedPrimer?.nameHe : selectedPrimer?.nameEn}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {calculations.primer.totalGrams.toLocaleString()}{isRTL ? ' גרם' : 'g'}
                        </p>
                      </div>
                    </div>
                    <div className="col-span-3 text-center">
                      <span className="inline-flex items-center justify-center px-2 py-1 rounded-md bg-warning/10 text-warning text-xs font-bold">
                        {calculations.primer.optimal.packsNeeded}× {calculations.primer.optimal.size}{isRTL ? 'ג' : 'g'}
                      </span>
                    </div>
                    <div className="col-span-4 text-end font-bold text-foreground">
                      ₪{calculations.primer.estimatedPrice.toLocaleString()}
                    </div>
                  </motion.div>

                  {/* Microlit Row */}
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                    className="grid grid-cols-12 gap-2 p-3 border-t border-border/30 items-center"
                  >
                    <div className="col-span-5 flex items-center gap-2">
                      <Layers className="w-4 h-4 text-accent flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-foreground text-sm truncate">
                          {isRTL ? calculations.microlit.productName : calculations.microlit.productNameEn}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {calculations.microlit.totalGrams.toLocaleString()}{isRTL ? ' גרם' : 'g'}
                        </p>
                      </div>
                    </div>
                    <div className="col-span-3 text-center">
                      <span className="inline-flex items-center justify-center px-2 py-1 rounded-md bg-accent/10 text-accent text-xs font-bold">
                        {calculations.microlit.optimal.packsNeeded}× {calculations.microlit.optimal.size}{isRTL ? 'ג' : 'g'}
                      </span>
                    </div>
                    <div className="col-span-4 text-end font-bold text-foreground">
                      ₪{calculations.microlit.estimatedPrice.toLocaleString()}
                    </div>
                  </motion.div>

                  {/* Sealer Row (if included) */}
                  {calculations.sealer && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 }}
                      className="grid grid-cols-12 gap-2 p-3 border-t border-border/30 items-center"
                    >
                      <div className="col-span-5 flex items-center gap-2">
                        <Shield className="w-4 h-4 text-success flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground text-sm truncate">
                            {isRTL ? 'סילר אקרוסיל' : 'Acrosil Sealer'}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {calculations.sealer.totalMl}{isRTL ? ' מ"ל' : 'ml'}
                          </p>
                        </div>
                      </div>
                      <div className="col-span-3 text-center">
                        <span className="inline-flex items-center justify-center px-2 py-1 rounded-md bg-success/10 text-success text-xs font-bold">
                          {calculations.sealer.optimal.packsNeeded}× {calculations.sealer.optimal.size}{isRTL ? 'מ"ל' : 'ml'}
                        </span>
                      </div>
                      <div className="col-span-4 text-end font-bold text-foreground">
                        ₪{calculations.sealer.estimatedPrice.toLocaleString()}
                      </div>
                    </motion.div>
                  )}

                  {/* Total Row */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="grid grid-cols-12 gap-2 p-3 border-t-2 border-accent/30 bg-accent/5 items-center"
                  >
                    <div className="col-span-8 font-bold text-foreground flex items-center gap-2">
                      <span className="text-lg">{isRTL ? 'סה"כ משוער' : 'Estimated Total'}</span>
                    </div>
                    <div className="col-span-4 text-end">
                      <span className="text-2xl font-bold text-accent">
                        ₪{calculations.totalPrice.toLocaleString()}
                      </span>
                    </div>
                  </motion.div>
                </div>

                {/* VAT Notice */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.9 }}
                  className="text-[11px] text-muted-foreground flex items-center gap-1.5 justify-center"
                >
                  <AlertCircle className="w-3 h-3" />
                  {isRTL ? 'המחירים אינם כוללים מע"מ' : 'Prices do not include VAT'}
                </motion.p>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Action Buttons - Stacked on Mobile */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="grid grid-cols-2 gap-2"
      >
        <Button variant="outline" size="lg" onClick={handleCopy} className="h-12">
          {copied ? <Check className="w-4 h-4 me-2" /> : <Copy className="w-4 h-4 me-2" />}
          <span className="text-sm">{t('copySummary')}</span>
        </Button>

        <Button variant="outline" size="lg" onClick={handlePrint} className="h-12">
          <Printer className="w-4 h-4 me-2" />
          <span className="text-sm">{t('printSummary')}</span>
        </Button>

        {/* Enhanced WhatsApp Button with animation */}
        <motion.div 
          className="col-span-2 relative"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <motion.div
            className="absolute inset-0 rounded-xl bg-success/30"
            animate={{ scale: [1, 1.05, 1], opacity: [0.3, 0, 0.3] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
          />
          <Button 
            size="lg" 
            onClick={handleWhatsApp} 
            className="w-full h-14 bg-gradient-to-r from-success to-success/80 hover:from-success/90 hover:to-success/70 text-white shadow-lg shadow-success/25 relative"
          >
            <MessageCircle className="w-6 h-6 me-3" />
            <span className="text-base font-semibold">
              {isRTL ? 'שלח הזמנה בוואטסאפ' : 'Send Order via WhatsApp'}
            </span>
          </Button>
        </motion.div>

        <Button variant="ghost" size="lg" onClick={onStartOver} className="h-10 col-span-2 text-muted-foreground">
          <RotateCcw className="w-4 h-4 me-2" />
          {t('startOver')}
        </Button>
      </motion.div>
    </div>
  );
};
